/* -----------------------------------------------------------------------
 * <copyright company="Microsoft Corporation">
 *   Copyright (c) Microsoft Corporation.  All rights reserved.
 * </copyright>
 * ----------------------------------------------------------------------- */

import "./App.scss";

import {
  ApplicationErrorStatus,
  ApplicationStatus,
  CssIncludes,
  JavaScriptIncludes,
  UHFFooterHTML,
  UHFHeaderHTML,
} from "./components/constants";
import {
  IPublicClientApplication,
  InteractionStatus,
} from "@azure/msal-browser";
import React, { useState } from "react";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import { Spinner, SpinnerSize, initializeIcons } from "@fluentui/react";
import { getAppUpdatedDate, getURLParams } from "./components/util";

import { Body } from "./components/body";
import BrandingFooterWrapper from "./components/footerTop/FooterTop";
import { ErrorBody } from "./components/errorBody";
import { Header } from "./components/header";
import NavbarCareersComponentWrapper from "./components/navbar/Navbar";
import { UHFBottomFooter } from "./components/uhf/footer";
import { UHFTopHeader } from "./components/uhf/header";
import axios from "axios";
import { config } from "./services/config";
import { logException } from "./services/telemetry/telemetryWrapper";
import { useIsAuthenticated } from "@azure/msal-react";
import { useMsal } from "@azure/msal-react";
import { usePlannedDownTimeInfo } from "./hooks/usePlannedDownTimeInfo";
import { useReferenceMetaData } from "./hooks/useReferenceMetaData";

initializeIcons();

const uhfAPI = `${process.env.REACT_APP_UHF_API}`;

export const App: React.FC = () => {
  const [UHFHeader, setUHFHeader] = React.useState("");
  const [UHFFooter, setUHFFooter] = React.useState("");
  const [cssIncludes, setCssIncludes] = React.useState("");
  const [scriptsLoaded, setScriptsLoaded] = React.useState(false);
  const [countCss, setCountCss] = React.useState(0);
  const [appStatus, setAppStatus] = useState<ApplicationStatus>(
    ApplicationStatus.REQUESTED
  );
  const [errorStatus, setErrorStatus] = useState<ApplicationErrorStatus>(
    ApplicationErrorStatus.INVALID
  );
  const [isPageReady, setIsPageReady] = useState(false);
  const [advisorName, setadvisorName] = React.useState("");

  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const { plannedDownTimeInfo, plannedDownTimeLoaded } =
    usePlannedDownTimeInfo();
  const { referenceMetaData, referenceMetaDataError, referenceMetaLoaded } =
    useReferenceMetaData(isAuthenticated, appStatus);
  const candidateFirstName =
    referenceMetaData.candidatePreferredFirstName ?? "";
  const candidateLastName = referenceMetaData.candidateLastName ?? "";
  const updatedDate =
    referenceMetaData.statusDate === ""
      ? referenceMetaData.statusDate
      : getAppUpdatedDate(new Date(referenceMetaData.statusDate));
  React.useEffect(() => {
    setIsPageReady(false);
    if (
      plannedDownTimeLoaded !== "false" &&
      plannedDownTimeInfo.serviceName &&
      plannedDownTimeInfo.serviceName.toLowerCase() !== "phenom"
    ) {
      setAppStatus(ApplicationStatus.ERROR);
      setErrorStatus(ApplicationErrorStatus.DOWN);
      setIsPageReady(true);
    } else if (inProgress === InteractionStatus.None && !isAuthenticated) {
      handleLogin(instance);
    } else if (isAuthenticated) {
      if (getURLParams().candidateId && getURLParams().referenceId) {
        if (
          referenceMetaLoaded !== "false" &&
          referenceMetaData.status !== ApplicationStatus.ERROR
        ) {
          setAppStatus(referenceMetaData.status as ApplicationStatus);
          setadvisorName(
            `${referenceMetaData.referenceFirstName} ${referenceMetaData.referenceLastName}`
          );
          setIsPageReady(true);
        } else if (referenceMetaLoaded !== "false") {
          setAppStatus(ApplicationStatus.ERROR);
          if (
            referenceMetaDataError.status === 403 &&
            referenceMetaDataError.statusCode === 410
          ) {
            setErrorStatus(ApplicationErrorStatus.EXPIRED);
            setIsPageReady(true);
          } else if (
            referenceMetaDataError.status === 401 ||
            referenceMetaDataError.status === 403 ||
            referenceMetaDataError.status === 400 ||
            referenceMetaDataError.status === 404
          ) {
            setErrorStatus(ApplicationErrorStatus.INVALID);
            setIsPageReady(true);
          } else {
            setErrorStatus(ApplicationErrorStatus.DOWN);
            setIsPageReady(true);
          }
        }
      } else {
        setAppStatus(ApplicationStatus.ERROR);
        setErrorStatus(ApplicationErrorStatus.INVALID);
        setIsPageReady(true);
      }
    }
  }, [
    isAuthenticated,
    inProgress,
    plannedDownTimeInfo,
    plannedDownTimeLoaded,
    referenceMetaData,
    referenceMetaDataError,
    referenceMetaLoaded,
  ]);

  function loadJSDependencies(jsIncludes: string) {
    const dummyElement = document.createElement("div");
    dummyElement.innerHTML = jsIncludes;
    const scriptArr = dummyElement.getElementsByTagName("script");

    for (const script of scriptArr) {
      //eslint-disable-next-line
      const scriptTag: any = document.createElement("script");
      scriptTag.src = script.getAttribute("src");
      document.getElementsByTagName("body")[0].appendChild(scriptTag);
    }
  }

  const loadStyle = function (url: string) {
    const link = document.createElement("link");
    link.type = "text/css";
    link.rel = "stylesheet";
    link.href = url;
    link.onload = () => {
      setCountCss((prevState: number) => prevState + 1);
    };
    const headScript = document.querySelector("script");
    if (headScript?.parentNode) {
      headScript.parentNode.insertBefore(link, headScript);
    }
  };

  React.useEffect(() => {
    if (scriptsLoaded == true) {
      const dummyElement = document.createElement("div");
      dummyElement.innerHTML = cssIncludes;
      const linkArr = dummyElement.getElementsByTagName("link");
      //eslint-disable-next-line
      let link: any;
      for (link of linkArr) {
        loadStyle(link.getAttribute("href"));
      }
    }
  }, [scriptsLoaded]);

  React.useEffect(() => {
    const cssLocal: string = sessionStorage.getItem(CssIncludes) || "";
    const jsLocal: string = sessionStorage.getItem(JavaScriptIncludes) || "";
    const headerLocal: string = sessionStorage.getItem(UHFHeaderHTML) || "";
    const footerLocal: string = sessionStorage.getItem(UHFFooterHTML) || "";
    const uhfuri: string = uhfAPI;

    if (
      cssLocal === "" ||
      jsLocal === "" ||
      headerLocal === "" ||
      footerLocal === ""
    ) {
      axios.get(uhfuri).then((shell) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(shell.data, "text/xml");

        setCssIncludes(
          xmlDoc.getElementsByTagName("cssIncludes")[0].textContent || ""
        );

        const jsIncludes =
          xmlDoc.getElementsByTagName("javascriptIncludes")[0].textContent ||
          "";
        const headerHtml =
          xmlDoc.getElementsByTagName("headerHtml")[0].textContent || "";
        const footerHtml =
          xmlDoc.getElementsByTagName("footerHtml")[0].textContent || "";

        sessionStorage.setItem(
          CssIncludes,
          xmlDoc.getElementsByTagName("cssIncludes")[0].textContent || ""
        );
        sessionStorage.setItem(JavaScriptIncludes, jsIncludes);
        sessionStorage.setItem(UHFHeaderHTML, headerHtml);
        sessionStorage.setItem(UHFFooterHTML, footerHtml);
        loadJSDependencies(jsIncludes);
        setUHFHeader(headerHtml);
        setUHFFooter(footerHtml);
        setScriptsLoaded(true);
      });
    } else {
      setCssIncludes(cssLocal);
      loadJSDependencies(jsLocal);
      setUHFHeader(headerLocal);
      setUHFFooter(footerLocal);
      setScriptsLoaded(true);
    }
    //eslint-disable-next-line
  }, []);

  return (
    <>
      {isPageReady ? (
        <Router>
          <div className="ms-ac-referenceview-app">
            <UHFTopHeader body={UHFHeader} />
            <NavbarCareersComponentWrapper />
            {appStatus !== ApplicationStatus.ERROR ? (
              <Header
                displayName={true}
                candidateName={`${candidateFirstName} ${candidateLastName}`}
              ></Header>
            ) : (
              <Header displayName={false} candidateName="" />
            )}
            <Switch>
              <Route exact path="/">
                {appStatus !== ApplicationStatus.ERROR ? (
                  <Body
                    advisorName={advisorName || ""}
                    candidateFirstName={candidateFirstName}
                    candidateLastName={candidateLastName}
                    updatedDate={updatedDate || ""}
                    status={appStatus}
                    setAppStatus={setAppStatus}
                  ></Body>
                ) : (
                  <ErrorBody errorStatus={errorStatus} />
                )}
              </Route>
              <Route path="*">
                <ErrorBody errorStatus={ApplicationErrorStatus.INVALID} />
              </Route>
            </Switch>
            <BrandingFooterWrapper />
            {countCss == 2 ? (
              <UHFBottomFooter body={UHFFooter} />
            ) : (
              <React.Fragment />
            )}
          </div>
        </Router>
      ) : (
        <div className="ms-ac-referenceview-app-main-loader-con">
          <Spinner
            size={SpinnerSize.large}
            label="Loading Microsoft Reference portal..."
          />
        </div>
      )}
    </>
  );
};

function handleLogin(instance: IPublicClientApplication) {
  instance.loginRedirect(config.loginRequestScope).catch((error) => {
    logException(error);
  });
}
